/**
 * Centralized API Endpoints Configuration
 * 
 * This file contains all API endpoint paths to avoid hardcoding throughout the codebase.
 * Use the `buildApiUrl` helper function to construct full URLs.
 */

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

/**
 * API Endpoints organized by resource
 */
export const API_ENDPOINTS = {
  // User endpoints
  users: {
    login: '/api/v1/users/login_weak',
    info: '/api/v1/users/info',
    preferences: '/api/v1/users/preferences',
    changePassword: '/api/v1/users/password',
    reset: (email: string) => `/api/v1/users/reset/${encodeURIComponent(email.trim())}`,
    recover: (email: string) => `/api/v1/users/recover/${encodeURIComponent(email.trim())}`,
    redeemAuthCode: (provider: string, tenantId?: string) => tenantId 
      ? `/api/v1/tenant/${tenantId}/${provider}/redeem_auth_code`
      : `/api/v1/users/${provider}/redeem_auth_code`,
  },

  // Tenant endpoints
  tenant: {
    get: '/api/v1/tenant',
    employees: (tenantId: number) => `${API_BASE_URL}/api/v1/tenant/${tenantId}/employees`,
    updateEmployeeRole: (tenantId: number, userId: number) => `${API_BASE_URL}/api/v1/tenant/${tenantId}/employee/${userId}/role`,
    removeEmployee: (tenantId: number, userId: number) => `${API_BASE_URL}/api/v1/tenant/${tenantId}/employee/${userId}`,
  },

  // Vehicle endpoints
  vehicle: {
    search: '/api/v1/vehicle/search',
    get: (id: number) => `/api/v1/vehicle/${id}`,
    getAll: '/api/v1/vehicles',
    bookings: {
      list: (vehicleId?: number) => vehicleId ? `/api/v1/vehicle/${vehicleId}/bookings` : '/api/v1/bookings',
      create: (vehicleId: number) => `/api/v1/vehicle/${vehicleId}/bookings/create`,
      get: (vehicleId: number, bookingId: number) => `/api/v1/vehicle/${vehicleId}/booking/${bookingId}`,
      update: (vehicleId: number, bookingId: number) => `/api/v1/vehicle/${vehicleId}/booking/${bookingId}`,
      delete: (vehicleId: number, bookingId: number) => `/api/v1/vehicle/${vehicleId}/booking/${bookingId}`,
    },
    reservations: {
      create: (vehicleId: number) => `/api/v1/vehicle/${vehicleId}/reservations/create`,
    },
    negotiation: {
      list: (vehicleId: number) => `/api/v1/vehicle/${vehicleId}/negotiations`,
      progress: (vehicleId: number, negotiationId: string) => `/api/v1/vehicle/${vehicleId}/negotiation/${negotiationId}/progress`,
      add: (vehicleId: number, negotiationId: string) => `/api/v1/vehicle/${vehicleId}/negotiation/${negotiationId}/add`,
      info: (vehicleId: number, negotiationId: string) => `/api/v1/vehicle/${vehicleId}/negotiation/${negotiationId}/info`,
    },
    offer: {
      negotiate: (vehicleId: number, offerId: number) => `/api/v1/vehicle/${vehicleId}/offer/${offerId}/negotiate`,
    },
    groupageTrip: {
      list: (vehicleId: number) => `/api/v1/vehicle/${vehicleId}/groupage-trips`,
      get: (vehicleId: number, tripId: number) => `/api/v1/vehicle/${vehicleId}/groupage-trip/${tripId}`,
      create: (vehicleId: number) => `/api/v1/vehicle/${vehicleId}/groupage-trip/create`,
      addNegotiation: (vehicleId: number, tripId: number) => `/api/v1/vehicle/${vehicleId}/groupage-trip/${tripId}/add-negotiation`,
      removeNegotiation: (vehicleId: number, tripId: number) => `/api/v1/vehicle/${vehicleId}/groupage-trip/${tripId}/remove-negotiation`,
      reorder: (vehicleId: number, tripId: number) => `/api/v1/vehicle/${vehicleId}/groupage-trip/${tripId}/reorder`,
      updateStatus: (vehicleId: number, tripId: number) => `/api/v1/vehicle/${vehicleId}/groupage-trip/${tripId}/status`,
    },
  },

  // Booking endpoints
  bookings: {
    list: '/api/v1/bookings',
    get: (id: number) => `/api/v1/bookings/${id}`,
    delete: (id: number) => `/api/v1/bookings/${id}`,
  },

  // Driver endpoints
  drivers: {
    list: '/api/v1/drivers',
    create: '/api/v1/drivers/create',
    get: (id: number) => `/api/v1/driver/${id}`,
    update: (id: number) => `/api/v1/driver/${id}`,
    delete: (id: number) => `/api/v1/driver/${id}`,
  },

  // Mail reminder endpoints
  mailReminders: {
    list: '/api/v1/mail_reminders',
    get: (id: number) => `/api/v1/mail_reminder/${id}`,
    create: '/api/v1/mail_reminders',
    update: (id: number) => `/api/v1/mail_reminder/${id}`,
    delete: (id: number) => `/api/v1/mail_reminder/${id}`,
  },

  // Billing endpoints
  billing: {
    portalLink: (tenantId: number) => `/api/v1/tenant/${tenantId}/portal_link`,
    subscribeLink: (tenantId: number) => `/api/v1/tenant/${tenantId}/subscribe_link`,
    syncSubscription: (tenantId: number) => `/api/v1/tenant/${tenantId}/sync_subscription`,
  },

  // MCP (Model Context Protocol) endpoints
  mcp: {
    generateKey: (tenantId: number) => `/api/v1/tenant/${tenantId}/mcp/generate_key`,
    revokeKey: (tenantId: number) => `/api/v1/tenant/${tenantId}/mcp/revoke_key`,
    generateOAuthCode: (tenantId: number) => `/api/v1/tenant/${tenantId}/mcp/generate_oauth_code`,
  },
  oauth: {
    token: '/api/v1/oauth/token',
  },

  // Offer endpoints
  offers: {
    analyze: '/api/v1/offers/analyze',
    create: '/api/v1/offers/create',
    reviewsByMessageId: (messageId: string) => `/api/v1/offers/reviews/${encodeURIComponent(messageId)}`,
  },

  // Stats endpoints
  stats: {
    offers: '/api/v1/stats/offers',
    users: '/api/v1/stats/users',
    dashboard: '/api/v1/stats/dashboard',
  },
} as const;

/**
 * Helper function to build full API URL
 * @param endpoint - The endpoint path (from API_ENDPOINTS)
 * @returns Full URL with base URL and endpoint
 */
export const buildApiUrl = (endpoint: string): string => `${API_BASE_URL}${endpoint}`;

/**
 * Get the API base URL
 */
export const getApiBaseUrl = (): string => API_BASE_URL;

